import { ReactNode } from "react";

import { IconButton } from "ui/icon-button";

import type { TouchpointVersionAttributes } from "hooks/api/use-touchpoint-versions";

import Icons from "assets";

export type DisabledActionMessage = {
  message: string;
  icon: ReactNode;
};

type GetDisabledActionMessageProps = {
  isBlockedByLock: boolean;
  isBlockedByInvalidFields: boolean;
  isBlockedByOpenActionItems: boolean;
  selectedVersion?: TouchpointVersionAttributes;
  handleOpenContentSettings: () => void;
  iconClassName?: string;
};

/**
 * Returns the message + icon for a blocked action, or `undefined` when no
 * blocker flag is set. Callers scope which blockers apply by passing `false`
 * for flags they don't care about (e.g. non-publish menu items pass `false`
 * for invalid-fields and action-items so those messages stay scoped to publish).
 */
export function getDisabledActionMessage({
  isBlockedByLock,
  isBlockedByInvalidFields,
  isBlockedByOpenActionItems,
  selectedVersion,
  handleOpenContentSettings,
  iconClassName,
}: GetDisabledActionMessageProps): DisabledActionMessage | undefined {
  const lockIconClassName = `text-gray-600 size-4${iconClassName ? ` ${iconClassName}` : ""}`;

  if (isBlockedByInvalidFields) {
    return {
      message: "Invalid or blank fields in Content settings must be corrected before publishing",
      icon: (
        <IconButton
          variant="text"
          color="error"
          size="large"
          aria-label="" // required but handled by tooltip
          onClick={handleOpenContentSettings}
        >
          <Icons.ExclamationCircle width={20} height={20} />
        </IconButton>
      ),
    };
  }

  if (isBlockedByOpenActionItems) {
    return {
      message: "Publishing is disabled due to open action items in comments",
      icon: <Icons.LockClosed className={lockIconClassName} />,
    };
  }

  if (isBlockedByLock) {
    const lockOwnerName = selectedVersion?.lockedBy
      ? `${selectedVersion.lockedBy.firstName ?? ""} ${selectedVersion.lockedBy.lastName ?? ""}`.trim()
      : "";

    return {
      message: lockOwnerName
        ? `This version is currently locked by ${lockOwnerName}`
        : "This version is currently locked",
      icon: <Icons.LockClosed className={lockIconClassName} />,
    };
  }

  return undefined;
}
